// Campaign tagging for App Store and Google Play links.
//
// Why this exists: the site sends ~123 store clicks a month (GA4 `app_store_click`), but the
// links carried no campaign parameters, so App Store Connect and Play Console had no way to
// tell those installs apart from any other source. That made the one number that actually
// matters, revenue per website visitor, impossible to compute: we could see the click and
// never the install behind it.
//
// Apple reads `ct` (campaign token) into App Analytics > Acquisition > Campaigns. Google Play
// reads an encoded `referrer` value into Play Console > Acquisition reports, and the install
// referrer is also readable by the app itself.
//
// Keep the campaign set SMALL and stable. Both stores treat these as report dimensions, and a
// per-URL campaign would shatter the reports into unusable cardinality. Placement granularity
// is enough to answer "which part of the site drives installs".

export type StorePlacement =
  | 'web-app-landing' // /cleanor, /cleanor-ios and the other product landings
  | 'web-blog' // in-article links
  | 'web-home' // homepage
  | 'web-account' // signed-in surfaces such as the PWA install panel
  | 'web-tools' // tool pages
  | 'web-other';

const SOURCE = 'cleanor.app';

/** Apple allows letters, digits, hyphen and underscore in a campaign token. */
function safeToken(value: string): string {
  return value.replace(/[^A-Za-z0-9_-]/g, '-').slice(0, 40);
}

/**
 * Tags an apps.apple.com URL so the install shows up under this campaign in App Analytics.
 * Existing query parameters are preserved.
 */
export function withAppStoreCampaign(url: string, placement: StorePlacement): string {
  return appendParams(url, { ct: safeToken(placement), mt: '8' });
}

/**
 * Tags a play.google.com URL. Play expects one `referrer` value holding an encoded UTM
 * string, not separate utm_* parameters.
 */
export function withPlayCampaign(url: string, placement: StorePlacement): string {
  const referrer = `utm_source=${SOURCE}&utm_medium=referral&utm_campaign=${safeToken(placement)}`;
  return appendParams(url, { referrer });
}

/** Tags either store by looking at the host, so callers do not have to branch. */
export function withStoreCampaign(url: string, placement: StorePlacement): string {
  if (url.includes('play.google.com')) return withPlayCampaign(url, placement);
  if (url.includes('apps.apple.com') || url.includes('itunes.apple.com')) {
    return withAppStoreCampaign(url, placement);
  }
  return url;
}

function appendParams(url: string, params: Record<string, string>): string {
  // Built without the URL class so this stays usable in any runtime and keeps the input
  // untouched when it already carries the parameter.
  const [base, hash = ''] = url.split('#');
  let next = base;
  for (const [key, value] of Object.entries(params)) {
    if (new RegExp(`[?&]${key}=`).test(next)) continue;
    next += `${next.includes('?') ? '&' : '?'}${key}=${encodeURIComponent(value)}`;
  }
  return hash ? `${next}#${hash}` : next;
}
